import React, { forwardRef } from 'react';
import { clsx } from 'clsx';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helperText?: string;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  /**
   * Class names applied to the outer wrapper. When omitted, sizing utilities
   * (w-*, max-w-*, min-w-*, flex-*, basis-*) detected in `className` are
   * forwarded to the wrapper so existing call sites that pass widths via
   * `className` keep working.
   */
  wrapperClassName?: string;
}

// Tailwind sizing utilities that, when passed in `className`, are intended for
// the outer wrapper rather than the inner <input>. Includes responsive prefixes.
const SIZE_PATTERN = /^(?:(?:sm|md|lg|xl|2xl):)?(?:w-|max-w-|min-w-|flex-(?:1|auto|none|grow|shrink)|basis-)/;

function splitSizeClasses(cn?: string): { size: string; rest: string } {
  if (!cn) return { size: '', rest: '' };
  const sizeTokens: string[] = [];
  const restTokens: string[] = [];
  for (const token of cn.split(/\s+/).filter(Boolean)) {
    (SIZE_PATTERN.test(token) ? sizeTokens : restTokens).push(token);
  }
  return { size: sizeTokens.join(' '), rest: restTokens.join(' ') };
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, helperText, leftIcon, rightIcon, className, wrapperClassName, ...props }, ref) => {
    const { size, rest } = splitSizeClasses(className);
    const wrapperCls = wrapperClassName ?? (size || 'w-full');
    return (
      <div className={wrapperCls}>
        {label && (
          <label className="label">
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 dark:text-gray-500">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            className={clsx(
              'input',
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              error && 'input-error',
              rest
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 dark:text-gray-500">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
        {helperText && !error && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{helperText}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export { Input };
export default Input;
